import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { SwitchWorkspaceDocument } from '@/graphql/generated/switch-workspace.generated';

export type SwitchWorkspaceTarget = {
    // React call sites pass the string id, the legacy iframes pass the numeric one.
    id?: string;
    idLegacy?: number;
};

/**
 * Switches workspace, then reloads the app.
 *
 * The reload is the design, not a shortcut. Workspace identity is ambient — a server-side
 * session cookie, and the same cookie again on the legacy iframe hosts — so every in-memory
 * copy of it goes stale the instant the mutation returns: the Apollo cache, PaymentProvider's
 * `hasFailedPayment` (held in `useState` and fed only by `onCompleted`), SocketProvider's
 * connection, both legacy iframes. Rebuilding all of that by hand inside a live SPA is what
 * the two previous implementations attempted, and each leaked a different piece of the old
 * workspace. A reload cannot leak any of it.
 *
 * It is not faster, and was not adopted to be. `appInitialLoad` costs ~3.3s on its own and
 * dominates the switch either way; the reload adds asset parsing on top of that. What it buys
 * is that there is nothing left to get wrong. Making the switch actually quick means making
 * that query quick, which is server-side work.
 *
 * Sends the browser to '/' instead of reloading in place: routes are filtered per workspace
 * (`usersPathFilterAccess`), so the current path may not exist in the destination.
 */
export function useSwitchWorkspace() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [switching, setSwitching] = useState(false);
    // A ref rather than `switching`, because two switchAccount messages arriving in the
    // same tick would both read a stale `false` from state.
    const switchInFlight = useRef(false);

    const [switchWorkspaceMutation] = useMutation(SwitchWorkspaceDocument);

    const switchWorkspace = useCallback(
        (target: SwitchWorkspaceTarget) => {
            if (switchInFlight.current) return;
            switchInFlight.current = true;
            setSwitching(true);

            switchWorkspaceMutation({
                variables: { id: target.id, idLegacy: target.idLegacy },
                onCompleted: () => {
                    // No cache teardown first: the Apollo cache is in-memory only — nothing
                    // in this app persists it — so the reload is what discards it.
                    // `switching` is deliberately left set; the page is on its way out and
                    // clearing it would only flicker the menu back to enabled.
                    window.location.href = '/';
                },
                onError: (e) => {
                    switchInFlight.current = false;
                    setSwitching(false);
                    enqueueSnackbar(t(e.message));
                },
            });
        },
        [switchWorkspaceMutation, enqueueSnackbar, t],
    );

    return { switchWorkspace, switching };
}
